export type LuggageCoverage = 'both-ways' | 'outbound-only' | 'inbound-only'

export type LuggageOptionLike = {
  optionId?: string | null
  id?: number | string | null
  nameCommercial?: string | null
  sellingPrice?: string | number | null
  quantity?: number | null
  familyId?: number | null
  tripInformation?: unknown
}

type TravellerPriceRow = { selected?: boolean; price?: number }

export type LuggageTripLeaf = {
  id?: string
  familyId?: number
  quantity?: number
  sellingPrice?: number | string
  details?: Record<string, unknown>
  tripInformation?: Record<string, unknown>
  pricePerTraveller?: TravellerPriceRow[]
  [key: string]: unknown
}

export function getLuggageDetails(
  leaf: LuggageTripLeaf | null | undefined
): Record<string, unknown> | undefined {
  if (!leaf) return undefined
  const details = leaf.details || leaf.tripInformation
  return details && typeof details === 'object' ? details : undefined
}

function travellerPriceRows(leaf: LuggageTripLeaf): TravellerPriceRow[] {
  const rows = leaf.pricePerTraveller || leaf['pricePerTraveller']
  return Array.isArray(rows) ? (rows as TravellerPriceRow[]) : []
}

function collectLuggageLeaves(raw: unknown): LuggageTripLeaf[] {
  const leaves: LuggageTripLeaf[] = []
  const visit = (node: unknown) => {
    if (!node) return
    if (Array.isArray(node)) {
      node.forEach(visit)
      return
    }
    if (typeof node !== 'object') return
    const obj = node as Record<string, unknown>
    const details = (obj.details || obj.tripInformation) as Record<string, unknown> | undefined
    const looksLikeLuggage =
      (details &&
        (details.weight != null ||
          details.description != null ||
          details.luggageType != null ||
          details.luggageType != null)) ||
      obj.pricePerTraveller != null ||
      obj.pricePerTraveller != null
    if (looksLikeLuggage) {
      leaves.push(obj as LuggageTripLeaf)
      return
    }
    Object.values(obj).forEach(visit)
  }
  visit(raw)
  return leaves
}

function encodedLuggageIdFromOptionId(optionId: string | null | undefined): string | null {
  if (!optionId) return null
  const pipeSegment = optionId.split('-').find(segment => segment.includes('|'))
  if (pipeSegment) return pipeSegment
  const match = optionId.match(/([A-Za-z]+\|\d+\|\d+)/)
  return match?.[1] ?? null
}

function nameMatchesLeaf(option: LuggageOptionLike, leaf: LuggageTripLeaf): boolean {
  const name = (option.nameCommercial || '').toLowerCase()
  if (!name) return false
  const details = getLuggageDetails(leaf)
  const description = String(details?.description || '').toLowerCase().trim()
  const weight = Number(details?.weight)
  if (!description || !Number.isFinite(weight)) return false
  const hasWeight =
    name.includes(`${weight}kg`) || name.includes(`${weight} kg`)
  return hasWeight && name.includes(description)
}

function toNumber(value: unknown): number | null {
  if (value == null || value === '') return null
  const n = typeof value === 'number' ? value : Number(String(value).replace(',', '.'))
  return Number.isFinite(n) ? n : null
}

/** Option sellingPrice is usually cents; tripInformation.sellingPrice is euros. */
function optionPriceInEuros(sellingPrice: string | number | null | undefined): number | null {
  const n = toNumber(sellingPrice)
  if (n == null) return null
  if (Number.isInteger(n)) return n / 100
  return n
}

function pricesAlmostEqual(a: number, b: number): boolean {
  return Math.abs(a - b) < 0.05
}

function selectedTravellerPrices(leaf: LuggageTripLeaf): number[] {
  return travellerPriceRows(leaf)
    .filter(row => row?.selected)
    .map(row => toNumber(row.price))
    .filter((price): price is number => price != null)
}

function priceMatchesLeaf(option: LuggageOptionLike, leaf: LuggageTripLeaf): boolean {
  const optionPrice = optionPriceInEuros(option.sellingPrice)
  if (optionPrice == null) return false

  const tripTotal = toNumber(leaf.sellingPrice)
  if (tripTotal != null && pricesAlmostEqual(optionPrice, tripTotal)) return true

  const optionQty = toNumber(option.quantity) ?? 0
  if (tripTotal != null && optionQty > 0 && pricesAlmostEqual(optionPrice * optionQty, tripTotal)) {
    return true
  }

  const selectedPrices = selectedTravellerPrices(leaf)
  if (selectedPrices.some(price => pricesAlmostEqual(optionPrice, price))) return true

  const selectedSum = selectedPrices.reduce((sum, price) => sum + price, 0)
  if (selectedPrices.length && pricesAlmostEqual(optionPrice, selectedSum)) return true

  return false
}

function optionIdentity(option: LuggageOptionLike): string {
  if (option.optionId) return `id:${option.optionId}`
  return `row:${option.nameCommercial ?? ''}|${option.sellingPrice ?? ''}|${option.quantity ?? ''}`
}

export function isLuggageLeafSelected(leaf: LuggageTripLeaf | null | undefined): boolean {
  if (!leaf) return false
  const prices = travellerPriceRows(leaf)
  if (prices.length > 0) {
    if (prices.some(row => row?.selected)) return true
    if (prices.every(row => !row?.selected) && Number(leaf.quantity) === 0) return false
  }
  return Number(leaf.quantity) > 0
}

export function getLuggageCoverage(
  details: Record<string, unknown> | null | undefined
): LuggageCoverage | null {
  if (!details) return null
  const outbound = details.outbound === true
  const inbound = details.inbound === true
  if (outbound && inbound) return 'both-ways'
  if (outbound) return 'outbound-only'
  if (inbound) return 'inbound-only'
  return null
}

/**
 * Pick the luggage tripInformation leaf that belongs to this option.
 * booking-lambda attaches the full luggage array to every familyId 82 option.
 */
export function resolveLuggageTripInfo(
  option: LuggageOptionLike,
  siblings?: LuggageOptionLike[]
): LuggageTripLeaf | null {
  const leaves = collectLuggageLeaves(option.tripInformation)
  if (!leaves.length) return null
  if (leaves.length === 1) return leaves[0]

  const encodedId = encodedLuggageIdFromOptionId(option.optionId)
  if (encodedId) {
    const byId = leaves.find(leaf => leaf.id === encodedId)
    if (byId) return byId
  }

  if (option.optionId) {
    const contained = leaves.filter(leaf => leaf.id && option.optionId!.includes(leaf.id))
    if (contained.length === 1) return contained[0]
  }

  const byName = leaves.filter(leaf => nameMatchesLeaf(option, leaf))
  if (byName.length === 1) return byName[0]

  const byPrice = leaves.filter(leaf => priceMatchesLeaf(option, leaf))
  if (byPrice.length === 1) return byPrice[0]

  if (siblings && siblings.length) {
    const prestId = Number(option.id)
    const samePrest = Number.isFinite(prestId) && prestId > 0
      ? siblings.filter(sibling => Number(sibling.id) === prestId)
      : siblings
    const identity = optionIdentity(option)
    const idx = samePrest.findIndex(sibling => optionIdentity(sibling) === identity)
    if (idx >= 0 && idx < leaves.length) return leaves[idx]
  }

  return leaves[0]
}
